import type { PlayerId, ProfileId, SkillBand } from './ids';

/** Cooperative rule is simultaneous-simple only; alternating turns are out of scope. */
export type PlayerMode = 'SINGLE' | 'COOP_SIMULTANEOUS_SIMPLE';

export const MAX_LOCAL_PLAYERS = 2;

/** Fixed physical lanes: Hero 1 left, Hero 2 right. Players never swap (MVP notes §6). */
export type PlayerLane = 'left' | 'right';

export const PLAYER_LANES: readonly PlayerLane[] = ['left', 'right'] as const;

/** Default child-facing labels when no short name is set. */
export const DEFAULT_PLAYER_LABELS: Readonly<Record<PlayerLane, string>> = {
  left: 'Hero 1',
  right: 'Hero 2',
};

/**
 * A player inside a running session. Two-player-ready from day one: single-player sessions
 * hold exactly one entry, always in the left lane.
 */
export interface PlayerRuntimeState {
  playerId: PlayerId;
  /** Absent for guest players, who keep no persistent progress. */
  profileId?: ProfileId;
  lane: PlayerLane;
  /** Optional short name; falls back to DEFAULT_PLAYER_LABELS[lane]. */
  shortName?: string;
  band: SkillBand;
  isGuest: boolean;
  present: boolean;
}

/** Alias used by the module contract (module spec §9). */
export type PlayerSessionState = PlayerRuntimeState;

export interface GuestPlayer {
  playerId: PlayerId;
  lane: PlayerLane;
  shortName?: string;
  band: SkillBand;
}

export function playerLabel(player: Pick<PlayerRuntimeState, 'lane' | 'shortName'>): string {
  return player.shortName ?? DEFAULT_PLAYER_LABELS[player.lane];
}
